// Condition -> emoji for the `weather_icon` measure format.
//
// Ported verbatim from get_weather_icon in fds-v2
// (project/fds_utils/fds_format_utils.py) — the same map every other surface
// renders this format through. The scheduler's config-driven charts are handed
// raw values plus a format code and do their formatting client-side, so there
// is no way for them to call the Python one; keep the two in step when either
// changes.
//
// Both of the source data's naming conventions are present and kept verbatim:
// sentence case ('Patchy rain possible') and title case ('Mostly Cloudy') come
// from different weather providers, and prod carries both.
//
// WeatherAPI has since renamed its "possible" conditions to "nearby" ('Patchy
// rain possible' -> 'Patchy rain nearby', 'Thundery outbreaks possible' ->
// 'Thundery outbreaks in nearby'). Both spellings are kept: history rows still
// carry the old ones.
export const WEATHER_ICONS: Record<string, string> = {
  'Light sleet showers': '🌨',
  'Moderate or heavy rain with thunder': '🌧',
  'Partly cloudy': '⛅',
  'Patchy light snow': '🌨️❄️',
  'Moderate snow': '🌨️❄️',
  'Heavy rain at times': '🌧',
  'Moderate rain': '🌧',
  Fog: '🌁',
  'Patchy heavy snow': '🌨️❄️',
  'Ice pellets': '🌨🧊',
  'Moderate or heavy snow with thunder': '🌨️❄️',
  'Patchy rain possible': '🌧',
  'Patchy rain nearby': '🌧',
  'Light drizzle': '🌧',
  'Light snow': '🌨️❄️',
  'Moderate or heavy sleet showers': '🌨',
  'Moderate or heavy snow showers': '🌨️❄️',
  Sunny: '☀️',
  'Patchy sleet possible': '🌨',
  'Patchy sleet nearby': '🌨',
  'Light sleet': '🌨',
  'Heavy rain': '🌧',
  Mist: '🌫',
  Blizzard: '🌪️❄️',
  'Moderate or heavy rain shower': '🌧',
  'Thundery outbreaks possible': '🌩🌪️',
  'Thundery outbreaks in nearby': '🌩🌪️',
  'Heavy freezing drizzle': '🌨',
  'Patchy freezing drizzle nearby': '🌨',
  'Light rain': '🌧',
  'Patchy snow possible': '🌨️❄️',
  'Patchy snow nearby': '🌨️❄️',
  'Patchy light drizzle': '🌧',
  'Light freezing rain': '🌨',
  'Light snow showers': '🌨️❄️',
  'Torrential rain shower': '🌧🌪️',
  'Patchy light rain with thunder': '🌧',
  Overcast: '☁️',
  'Freezing fog': '🌫',
  'Moderate rain at times': '🌧',
  'Patchy moderate snow': '🌨',
  Cloudy: '☁️',
  'Heavy snow': '🌨️❄️',
  'Freezing drizzle': '🌨',
  'Blowing snow': '🌨️❄️',
  'Patchy light rain': '🌧',
  'Patchy light snow with thunder': '🌨️❄️',
  'Moderate or heavy sleet': '🌨',
  'Light rain shower': '🌧',
  'Moderate or heavy freezing rain': '🌨',
  'Partly Cloudy': '⛅',
  'Light Rain': '🌧',
  'Light Drizzle': '🌧',
  'Heavy Rain': '🌧',
  'Light Sleet': '🌨',
  'Light Freezing Rain': '🌨',
  'Freezing Drizzle': '🌨',
  'Blowing Snow': '🌨️',
  'Light Rain Shower': '🌨️',
  'Light Snow': '🌨️',
  'Heavy Snow': '🌨️❄️',
  'Mostly Cloudy': '☁️',
  Fair: '☀️',
  'Cloudy / Windy': '☁️',
  'Fair / Windy': '☀️',
  Drizzle: '🌧️',
  'Patches of Fog': '🌫️',
  Rain: '🌧️',
  'Small Hail': '🌨️',
  'Light Snow / Windy': '❄️💨',
  'Mostly Cloudy / Windy': '☁️',
  'Heavy T-Storm': '⛈️⚡',
  'Light Freezing Drizzle': '🌧️❄️',
  'Haze / Windy': '🌫️💨',
  Thunder: '⚡',
  'Shallow Fog': '🌫️',
  'Thunder in the Vicinity': '⚡🌩️',
  'Heavy Rain / Windy': '🌧️💨',
  'Wintry Mix / Windy': '🌨️💨',
  'Heavy Snow / Windy': '❄️💨',
  'Blowing Snow / Windy': '🌬️❄️',
  'T-Storm / Windy': '⛈️💨',
  'Light Snow Grains': '🌨️',
  'Light Snow with Thunder': '🌨️⚡',
  'Blowing Dust / Windy': '🌬️🌪️',
  'Blowing Dust': '🌪️',
  'Partial Fog': '🌫️🌥️',
  'Snow and Sleet / Windy': '❄️🌧️💨',
  'Drifting Snow / Windy': '🌬️❄️',
  'Drifting Snow': '🌨️',
  'Snow and Thunder': '❄️⚡',
  'Rain Shower': '🌦️',
  'Thunder / Windy': '⚡💨',
  'Rain Shower / Windy': '🌦️💨',
  'Squalls / Windy': '🌪️💨',
  'Heavy Drizzle / Windy': '🌧️💨',
  'Widespread Dust': '🌪️🌫️',
  'Heavy Snow with Thunder': '❄️⚡',
  'Mist / Windy': '🌫️💨',
  'Sand / Dust Whirlwinds': '🌪️',
  'Sand / Dust Whirls Nearby': '🌪️👀',
  'Smoke / Windy': '🌫️💨',
  Squalls: '🌪️',
  'Heavy Rain Shower': '🌧️⛈️',
  'Widespread Dust / Windy': '🌪️💨',
  Duststorm: '🌪️',
  'Blowing Sand / Windy': '🌬️🏜️',
  Hail: '🌨️',
  'Heavy Freezing Drizzle': '🌧️❄️',
  'Thunder and Small Hail': '⛈️🌨️',
  'Rain / Fog': '🌧️🌫️',
  'Heavy Rain / Windy / Fog': '🌧️💨🌫️',
  'Snow / Fog': '❄️🌫️',
  'Light Freezing Rain / Fog': '🌧️❄️🌫️',
  'Heavy Freezing Rain': '🌧️❄️',
  'Snow Shower / Windy': '🌨️💨',
  'Blowing Snow Nearby': '❄️🌬️',
  'Ice Crystals': '❄️✨',
  'Rain and Snow / Windy': '🌧️❄️💨',
  'Rain and Sleet': '🌧️❄️',
  'Heavy Duststorm': '🌪️',
  'Heavy Sleet / Windy': '🌨️💨',
  'Wintry Mix': '🌨️❄️',
  Haze: '🌫️',
  'Unknown Precipitation': '❓🌧️',
  'Partly Cloudy / Windy': '☁️',
  'Freezing Rain': '🌧️❄️',
  'T-Storm': '⛈️',
  Snow: '❄️',
  'Light Rain / Windy': '🌧️💨',
  'Showers in the Vicinity': '🌦️👀',
  'Heavy Drizzle': '🌧️',
  'Drizzle and Fog': '🌧️🌫️',
  'Rain / Windy': '🌧️💨',
  Sleet: '🌨️',
  'Snow and Sleet': '❄️🌧️',
  'Thunder and Hail': '⚡🌨️',
  'Light Snow Shower / Windy': '🌨️💨',
  'Light Snow Shower': '🌨️',
  'Snow / Windy': '❄️💨',
  'Light Freezing Rain / Windy': '🌧️❄️💨',
  'Fog / Windy': '🌫️💨',
  'Light Rain with Thunder': '🌧️⚡',
  Smoke: '🌫️',
  'Light Snow and Sleet': '🌨️❄️',
  'Light Drizzle / Windy': '🌧️💨',
  'Heavy T-Storm / Windy': '⛈️💨',
  'Sleet / Windy': '🌨️💨',
  'Drizzle / Windy': '🌧️💨',
  'Thunder / Wintry Mix': '⚡❄️🌨️',
  'Thunder / Wintry Mix / Windy': '⚡❄️🌨️💨',
  'Freezing Drizzle / Windy': '🌧️❄️💨',
  'Light Sleet / Windy': '🌨️💨',
  'Drizzle and Fog / Windy': '🌧️🌫️💨',
  'Light Rain Shower / Windy': '🌦️💨',
  'Light Snow and Sleet / Windy': '🌨️❄️💨',
  'Sand / Dust Whirlwinds / Windy': '🌪️💨',
  'Thunder and Hail / Windy': '⚡🌨️💨',
  'Patches of Fog / Windy': '🌫️💨',
  'Freezing Rain / Windy': '🌧️❄️💨',
  'Heavy Sleet': '🌨️❄️',
  'Rain / Freezing Rain': '🌧️❄️',
  Sandstorm: '🌪️',
  Tornado: '🌪️',
  'Heavy Rain / Fog': '🌧️🌫️',
  'Light Rain / Fog': '🌧️🌫️',
  'Unknown Precipitation / Fog': '❓🌧️🌫️',
  'Snow / Windy / Fog': '❄️💨🌫️',
  'Heavy Snow / Fog': '❄️🌫️',
  'Light Snow / Fog': '❄️🌫️',
  'Heavy Snow / Windy / Fog': '❄️💨🌫️',
  'Light Snow / Windy / Fog': '❄️💨🌫️',
  'Wintry Mix / Fog': '🌨️❄️🌫️',
  'Small Hail / Windy': '🌨️💨',
  'Funnel Cloud': '☁️',
  'Light Sleet and Thunder': '🌨️⚡',
  'Rain and Sleet / Windy': '🌧️❄️💨',
  'Duststorm / Windy': '🌪️💨',
  'Freezing Rain / Fog': '🌧️❄️🌫️',
}

// 174 conditions, matching the backend map entry for entry.
